import { AGWASuiteApiClientBase, ApiResponse } from '../agwasuite-api';
import type {
  EndCurrentTabSessionResult,
  IssueTabSessionResult,
} from '../agwa-api-model';

/** How long `end()` waits for the server before the caller signs out without it. */
export const TAB_SESSION_END_TIMEOUT_MS = 5_000;

/**
 * Client for the per-browser-tab session endpoint.
 *
 * Its own client rather than a method on `ApiLogin`, because it is not a login: the caller is
 * already authenticated when it calls this. Everything `ApiLogin` calls a session is an ACCOUNT,
 * and putting a tab session next to those is how the two got conflated in the first place.
 */
export class ApiTabSession extends AGWASuiteApiClientBase {
  /**
   * Asks the server for a session for this tab.
   *
   * <p>NOT idempotent on the server: every call issues a new session, because two tabs are
   * indistinguishable before either has one. Call it through
   * `services/tab-session.ts#ensureTabSessionId`, which asks once per tab and remembers the
   * answer; calling it directly on every request would mint a session per request.</p>
   */
  async issue(): Promise<ApiResponse<IssueTabSessionResult>> {
    return this.post<Record<string, never>, IssueTabSessionResult>(
      '/api/TabSession/issue',
      {}
    );
  }

  /**
   * Ends this tab's own session on the server and releases the locks it held, leaving the
   * account's other tabs and its cookie alone (D-143). Idempotent: a session already ended answers
   * `sessionEnded: false`. The session ended is the one this client's tab-session header carries,
   * proved by the server against the account the request authenticates as.
   *
   * <p>ONE attempt, bounded to a few seconds. Its caller is a tab signing out because its sign-in
   * named somebody else; the client default (three attempts of 30 seconds) would keep that tab in
   * the app as a person the server no longer confirms for over a minute. If the end does not
   * answer in time the tab signs out anyway and the locks are left to their lease.</p>
   */
  async end(): Promise<ApiResponse<EndCurrentTabSessionResult>> {
    return this.request<EndCurrentTabSessionResult>(
      'POST',
      '/api/TabSession/end',
      {},
      { retry: false, timeoutMs: TAB_SESSION_END_TIMEOUT_MS }
    );
  }
}
